/**
 * One page's comments, kept current outside React (RFC 0339).
 *
 * The host-realm app bridge answers `comments.list` synchronously and lets an
 * app subscribe to changes. Comments now live in the comments document a page
 * links by `commentsId`, so this follows that link (and a code file's, on the
 * project), holds the documents it finds, and tells subscribers when any of
 * them, the page, or the project changes. `map()` merges them with what the
 * project still holds from before the move, the same rule as `loadComments`.
 */

import {
  type DocHandle,
  isValidDocumentId,
  type Repo,
} from '@automerge/automerge-repo/slim'
import {
  type CommentMap,
  linkedCommentsIds,
  mergeComments,
} from '@mod/core/model'
import { type HeldDocs, holdDocument } from '../world/repo/held-docs'
import { followDocument } from './followDocument'

export interface CommentWatch {
  map(): CommentMap
  subscribe(fn: () => void): () => void
  stop(): void
}

export function watchComments(
  repo: Repo,
  project: DocHandle<unknown>,
  subjectId: string,
  ownership?: HeldDocs,
): CommentWatch {
  const listeners = new Set<() => void>()
  const held = new Map<string, DocHandle<unknown>>()
  /** Every document being waited for or held, by id, with its unsubscribe. */
  const following = new Map<string, () => void>()
  let owner: DocHandle<unknown> | null = isValidDocumentId(subjectId)
    ? null
    : project
  let stopped = false
  const releases: (() => void)[] = []
  const hold = (id: string) => {
    releases.push(ownership ? ownership.hold(id) : holdDocument(repo, id))
  }
  // The project is listened to for as long as the watch runs (RFC 0317 3d).
  hold(project.documentId)
  const notify = () => {
    for (const fn of listeners) fn()
  }
  // A change to the owner may be its first comment linking a new document.
  const follow = () => {
    if (stopped || !owner) return
    for (const id of linkedCommentsIds(owner.doc())) {
      if (following.has(id)) continue
      hold(id)
      // Followed, not found once: a comments document linked before it has
      // synced here arrives later, and is still heard.
      following.set(
        id,
        followDocument(repo, id, (h) => {
          if (stopped || held.has(id)) return
          held.set(id, h)
          h.on('change', notify)
          notify()
        }),
      )
    }
  }
  const onOwner = () => {
    follow()
    notify()
  }
  if (owner) {
    owner.on('change', onOwner)
    follow()
  } else {
    hold(subjectId)
    following.set(
      subjectId,
      followDocument(repo, subjectId, (h) => {
        if (stopped) return
        owner = h
        h.on('change', onOwner)
        follow()
      }),
    )
  }
  // The project's own leftovers, and a code file's comments on the project.
  if (owner !== project) project.on('change', notify)

  return {
    map: () =>
      mergeComments(
        project.doc(),
        [...held.values()].map((h) => h.doc()),
      ),
    subscribe: (fn) => {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    stop: () => {
      stopped = true
      owner?.off('change', onOwner)
      if (owner !== project) project.off('change', notify)
      for (const off of following.values()) off()
      following.clear()
      for (const h of held.values()) h.off('change', notify)
      held.clear()
      listeners.clear()
      for (const release of releases.splice(0)) release()
    },
  }
}
